import { Link } from "react-router-dom";
import { ArrowRight, Clock } from "lucide-react";

const CASES = [
  {
    n: "01",
    title: "Reprise en main d'un Core IT en réorganisation profonde",
    contexte:
      "Un éditeur logiciel de premier plan engagé dans une transformation majeure.",
    enjeu:
      "Remettre en ordre un Core IT désorganisé et restaurer la confiance de la direction et des équipes.",
    intervention:
      "Reprise du pilotage de la DSI, restructuration des processus, renégociation des contrats fournisseurs.",
    resultat:
      "Productivité des équipes nettement améliorée ; coûts réduits sur les contrats-clés.",
  },
  {
    n: "02",
    title: "Programme stratégique remis en trajectoire",
    contexte:
      "Programme BI critique en retard significatif, en pleine migration d'infrastructures.",
    enjeu:
      "Réaligner le périmètre et restaurer la crédibilité du programme auprès du comex.",
    intervention:
      "Reprise du pilotage, réalignement des jalons, orchestration des migrations et du décommissionnement.",
    resultat:
      "Programme remis sous contrôle, gouvernance restaurée, trajectoire clarifiée.",
  },
  {
    n: "03",
    title: "Programme critique à échéance imposée",
    contexte:
      "Livraison d'une plateforme stratégique à date fixe, sous très forte visibilité.",
    enjeu:
      "Tenir la date, la qualité et la gouvernance sans compromis.",
    intervention:
      "Reprise de la gouvernance agile, cadrage des jalons et des risques, coordination des parties prenantes.",
    resultat:
      "Mise en production dans les délais et au niveau de qualité attendu.",
  },
];

const DIAGNOSTICS = [
  {
    label: "Diagnostic SI",
    duration: "10 minutes",
    desc: "Une lecture claire de la santé de votre DSI : gouvernance, sécurité, exécution.",
    href: "/diagnostic",
    testid: "home-diagnostic-si-cta",
  },
  {
    label: "Diagnostic PCA",
    duration: "5 minutes",
    desc: "La résilience réelle de votre SI face à une rupture majeure : tests, restauration, reprise.",
    href: "/diagnostic-pca",
    testid: "home-diagnostic-pca-cta",
  },
];

export default function Interventions() {
  return (
    <section
      id="interventions-cas"
      data-testid="interventions-section"
      className="py-24 sm:py-36 px-6 sm:px-10 lg:px-16 bg-[#FAF8F5]"
    >
      <div className="max-w-7xl mx-auto">
        <div className="reveal max-w-3xl mb-20">
          <p className="eyebrow flex items-center mb-6">
            <span className="gold-line" />
            Exemples de reprises en main
          </p>
          <h2 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl text-[#0B132B] leading-[1.1]">
            Trois situations. <span className="italic">Trois reprises en main.</span>
          </h2>
          <p className="mt-6 text-[#4A5568] leading-relaxed font-light text-lg">
            Missions récentes, contextes anonymisés. Rien n'a été enjolivé.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-px bg-[#E2DCD0] border border-[#E2DCD0]">
          {CASES.map((c, i) => (
            <article
              key={c.n}
              data-testid={`exemple-case-${i}`}
              className="reveal bg-[#FAF8F5] p-10 sm:p-12 flex flex-col"
              style={{ transitionDelay: `${i * 100}ms` }}
            >
              <span className="font-serif italic text-5xl sm:text-6xl text-[#C8A97E]/50 leading-none mb-6">
                {c.n}
              </span>
              <h3 className="font-serif text-2xl sm:text-3xl text-[#0B132B] mb-8 leading-tight">
                {c.title}
              </h3>

              <div className="space-y-6 flex-1">
                <div>
                  <p className="eyebrow mb-2 flex items-center gap-2">
                    <span className="gold-line" />
                    Contexte
                  </p>
                  <p className="font-serif italic text-[#16234A] text-base sm:text-lg leading-snug font-light">
                    {c.contexte}
                  </p>
                </div>
                <div className="pt-6 border-t border-[#E2DCD0]">
                  <p className="eyebrow mb-2 flex items-center gap-2">
                    <span className="gold-line" />
                    Enjeu
                  </p>
                  <p className="text-[#171D2A] font-light text-base leading-relaxed">
                    {c.enjeu}
                  </p>
                </div>
                <div className="pt-6 border-t border-[#E2DCD0]">
                  <p className="eyebrow mb-2 flex items-center gap-2">
                    <span className="gold-line" />
                    Intervention
                  </p>
                  <p className="text-[#171D2A] font-light text-base leading-relaxed">
                    {c.intervention}
                  </p>
                </div>
                <div className="pt-6 border-t border-[#E2DCD0]">
                  <p className="eyebrow mb-2 flex items-center gap-2">
                    <span className="gold-line" />
                    Résultat
                  </p>
                  <p className="font-serif italic text-[#C8A97E] text-base sm:text-lg leading-snug">
                    {c.resultat}
                  </p>
                </div>
              </div>
            </article>
          ))}
        </div>

        {/* Deux diagnostics selon votre besoin */}
        <div data-testid="home-diagnostics-block" className="reveal mt-16">
          <p className="eyebrow flex items-center mb-8">
            <span className="gold-line" />
            Deux diagnostics selon votre besoin
          </p>
          <div className="grid md:grid-cols-2 gap-px bg-[#E2DCD0] border border-[#E2DCD0]">
            {DIAGNOSTICS.map((d) => (
              <Link
                key={d.href}
                to={d.href}
                data-testid={d.testid}
                className="group bg-[#F0ECE1] p-10 sm:p-12 flex flex-col hover:bg-[#0B132B] transition-colors duration-500"
              >
                <p className="flex items-center gap-3 text-xs uppercase tracking-[0.16em] text-[#8B7355] group-hover:text-[#C8A97E] transition-colors mb-4">
                  <Clock size={13} />
                  {d.duration}
                </p>
                <h3 className="font-serif font-light text-2xl sm:text-3xl text-[#0B132B] group-hover:text-[#F0ECE1] transition-colors leading-tight mb-3">
                  {d.label}
                </h3>
                <p className="text-[#4A5568] group-hover:text-[#F0ECE1]/75 transition-colors font-light leading-relaxed flex-1">
                  {d.desc}
                </p>
                <span className="mt-8 inline-flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-[#0B132B] group-hover:text-[#C8A97E] transition-colors">
                  Lancer le diagnostic
                  <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
